import { useEffect, useReducer, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Minus,
  Plus,
  Search,
  X,
  Tag,
  RotateCcw,
  ArrowRight,
  CheckCircle,
  Zap,
  LayoutGrid,
  AlertCircle,
} from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listMenuItems } from "@/lib/api/admin";
import type { BuyXGetYDeal, BuyXGetYDiscountType, MenuItem } from "@/lib/api/types";
import {
  validateRawSchedule,
  formToSchedule,
  scheduleToForm,
  type ScheduleFormState,
} from "@/lib/utils/schedule";
import { DealScheduleInput } from "@/components/schedule/DealScheduleInput";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { DiscardDialog } from "@/components/DiscardDialog";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCents } from "@/lib/utils/number";
import { cn } from "@/lib/utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 20;

/**
 * Mirrors the backend's `rule_label` formula exactly (see the Multi Buy / BuyXGetY contract):
 * - percent, value>=100 → "Buy {buy}, get {get} free"
 * - percent, else       → "Buy {buy}, get {get} at {value}% off"
 * - amount              → "Buy {buy}, get {get} at ${value/100} off" (value is cents off per get unit)
 */
function multiBuyRuleLabel(
  buy: number,
  get: number,
  discountType: BuyXGetYDiscountType,
  discountValue: number
): string {
  if (discountType === "amount") {
    return `Buy ${buy}, get ${get} at $${(discountValue / 100).toFixed(2)} off`;
  }
  if (discountValue >= 100) {
    return `Buy ${buy}, get ${get} free`;
  }
  return `Buy ${buy}, get ${get} at ${discountValue}% off`;
}

// ─── Form state ─────────────────────────────────────────────────────────────────

type MultiBuyDealFormState = {
  name: string;
  buyQuantity: number;
  getQuantity: number;
  discountType: BuyXGetYDiscountType;
  /** percent: whole-number string "0-100"; amount: dollar string like "2.00" (dollars off per get unit). */
  discountValue: string;
  maxClaimsPerCustomerPerDay: string;
  schedule: ScheduleFormState;
  status: "active" | "inactive";
};

type FormAction =
  | { type: "set"; field: "name" | "discountValue" | "maxClaimsPerCustomerPerDay"; value: string }
  | { type: "setQuantity"; field: "buyQuantity" | "getQuantity"; value: number }
  | { type: "setDiscountType"; value: BuyXGetYDiscountType }
  | { type: "setStatus"; value: "active" | "inactive" }
  | { type: "setSchedule"; value: ScheduleFormState }
  | { type: "reset"; item?: BuyXGetYDeal };

function discountValueToString(item: BuyXGetYDeal): string {
  return item.discount_type === "amount"
    ? (item.discount_value / 100).toFixed(2)
    : String(item.discount_value);
}

function initForm(item?: BuyXGetYDeal): MultiBuyDealFormState {
  if (!item) {
    return {
      name: "",
      buyQuantity: 1,
      getQuantity: 1,
      discountType: "percent",
      discountValue: "",
      maxClaimsPerCustomerPerDay: "",
      schedule: scheduleToForm(),
      status: "active",
    };
  }
  return {
    name: item.name,
    buyQuantity: item.buy_quantity,
    getQuantity: item.get_quantity,
    discountType: item.discount_type,
    discountValue: discountValueToString(item),
    maxClaimsPerCustomerPerDay:
      item.max_claims_per_customer_per_day != null
        ? String(item.max_claims_per_customer_per_day)
        : "",
    schedule: scheduleToForm(item.schedule),
    status: item.status,
  };
}

function formReducer(state: MultiBuyDealFormState, action: FormAction): MultiBuyDealFormState {
  switch (action.type) {
    case "set":
      return { ...state, [action.field]: action.value };
    case "setQuantity":
      return { ...state, [action.field]: action.value };
    case "setDiscountType":
      // Clear the value on type switch — cents and percent aren't interchangeable.
      return { ...state, discountType: action.value, discountValue: "" };
    case "setStatus":
      return { ...state, status: action.value };
    case "setSchedule":
      return { ...state, schedule: action.value };
    case "reset":
      return initForm(action.item);
    default:
      return state;
  }
}

// ─── Live preview economics (adapted to admin's cents-off-per-get-unit semantics) ──

type PreviewData = {
  fullValueCents: number;
  discountCents: number;
  revenueCents: number;
  addToCartCount: number;
};

/**
 * `previewValue` is already parsed: percent as a whole number, amount as cents-off per get unit.
 */
function computePreview(
  item: MenuItem,
  buyQty: number,
  getQty: number,
  discountType: BuyXGetYDiscountType,
  previewValue: number
): PreviewData | null {
  if (isNaN(previewValue) || previewValue <= 0) return null;
  const fullValueCents = (buyQty + getQty) * item.price_cents;
  let discountCents = 0;
  if (discountType === "percent") {
    if (previewValue > 100) return null;
    discountCents = Math.round((getQty * item.price_cents * previewValue) / 100);
  } else {
    // previewValue = cents off per get unit; can't exceed the item's price.
    if (previewValue > item.price_cents) return null;
    discountCents = getQty * previewValue;
  }
  if (discountCents <= 0) return null;
  return {
    fullValueCents,
    discountCents,
    revenueCents: fullValueCents - discountCents,
    addToCartCount: buyQty + getQty,
  };
}

// ─── Multi Buy Deal Form Dialog (i.e. BOGO) ────────────────────────────────

type MultiBuyDealFormDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  storeId: string;
  item?: BuyXGetYDeal;
  onSave: (data: {
    name: string;
    menu_item_id: string;
    buy_quantity: number;
    get_quantity: number;
    discount_type: BuyXGetYDiscountType;
    discount_value: number;
    max_claims_per_customer_per_day: number | null;
    schedule: Record<string, { open: string; close: string }[]>;
    status: "active" | "inactive";
  }) => void;
  isPending: boolean;
  apiError?: string | null;
};

export function MultiBuyDealFormDialog({
  open,
  onOpenChange,
  storeId,
  item,
  onSave,
  isPending,
  apiError,
}: MultiBuyDealFormDialogProps) {
  const user = useAdminAuth();
  const isEditing = !!item;
  const [state, dispatch] = useReducer(formReducer, undefined, () => initForm(item));
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(item?.menu_item ?? null);
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [maxClaimsError, setMaxClaimsError] = useState<string | null>(null);
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [scheduleFormError, setScheduleFormError] = useState<string | null>(null);

  const [itemSearchInput, setItemSearchInput] = useState("");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const itemSearchRef = useRef<HTMLDivElement>(null);

  // Dismiss the item search results when clicking anywhere outside the search box + dropdown.
  useEffect(() => {
    if (!open || itemSearchQuery === "") return;
    function handlePointerDown(e: PointerEvent) {
      if (!itemSearchRef.current?.contains(e.target as Node)) {
        setItemSearchQuery("");
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open, itemSearchQuery]);

  const { data: searchResults, isFetching: searchLoading } = useQuery({
    queryKey: ["menu-items", storeId, "multi-buy-deal-search", itemSearchQuery],
    queryFn: () => listMenuItems(user!, storeId, { search: itemSearchQuery, limit: 20 }),
    enabled: !!user && open && itemSearchQuery.trim() !== "",
  });

  // Reset all local state whenever the dialog opens for a (possibly new) item.
  useEffect(() => {
    if (!open) return;
    dispatch({ type: "reset", item });
    setSelectedItem(item?.menu_item ?? null);
    setDirty(false);
    setHasAttemptedSubmit(false);
    setNameError(null);
    setItemError(null);
    setDiscountError(null);
    setMaxClaimsError(null);
    setScheduleErrors({});
    setScheduleFormError(null);
    setItemSearchInput("");
    setItemSearchQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  function selectItem(mi: MenuItem) {
    setDirty(true);
    setItemError(null);
    setSelectedItem(mi);
    setItemSearchInput("");
    setItemSearchQuery("");
  }

  function setQuantity(field: "buyQuantity" | "getQuantity", value: number) {
    setDirty(true);
    dispatch({
      type: "setQuantity",
      field,
      value: Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, value)),
    });
  }

  // previewValue: percent whole number, or amount as cents-off per get unit (dollars * 100).
  const previewValue =
    state.discountType === "amount"
      ? Math.round(parseFloat(state.discountValue) * 100)
      : parseInt(state.discountValue, 10);
  const hasValidPreviewValue = !isNaN(previewValue) && previewValue > 0;
  const rulePreview = hasValidPreviewValue
    ? multiBuyRuleLabel(state.buyQuantity, state.getQuantity, state.discountType, previewValue)
    : null;

  const preview =
    selectedItem && hasValidPreviewValue
      ? computePreview(
          selectedItem,
          state.buyQuantity,
          state.getQuantity,
          state.discountType,
          previewValue
        )
      : null;

  // Ready / next-step for the validation footer.
  const hasValidDiscount = (() => {
    if (!hasValidPreviewValue) return false;
    if (state.discountType === "percent") return previewValue >= 1 && previewValue <= 100;
    // amount: cents off per get unit, must not exceed the item's price.
    return selectedItem ? previewValue <= selectedItem.price_cents : previewValue >= 1;
  })();
  const hasSchedule = Object.keys(formToSchedule(state.schedule)).length > 0;
  const isReadyToPublish = !!selectedItem && hasValidDiscount && hasSchedule;
  const nextRequiredStep = !selectedItem
    ? "Select an applicable item"
    : !hasValidDiscount
      ? 'Set the discount for the "get" unit'
      : !hasSchedule
        ? "Choose at least one active day"
        : null;

  function tryClose() {
    if (dirty || itemSearchInput.trim() !== "") {
      setDiscardOpen(true);
    } else {
      onOpenChange(false);
    }
  }

  function handleSave() {
    setHasAttemptedSubmit(true);
    setNameError(null);
    setItemError(null);
    setDiscountError(null);
    setMaxClaimsError(null);
    setScheduleErrors({});
    setScheduleFormError(null);

    const name = state.name.trim();
    if (name.length < 2 || name.length > 100) {
      setNameError("Name must be between 2 and 100 characters.");
      return;
    }

    if (!selectedItem) {
      setItemError("Select a menu item.");
      return;
    }

    let discountValue: number;
    if (state.discountType === "percent") {
      discountValue = parseInt(state.discountValue, 10);
      if (isNaN(discountValue) || discountValue < 1 || discountValue > 100) {
        setDiscountError("Enter a percentage between 1 and 100.");
        return;
      }
    } else {
      discountValue = Math.round(parseFloat(state.discountValue) * 100);
      if (isNaN(discountValue) || discountValue < 1) {
        setDiscountError("Enter an amount greater than $0.00.");
        return;
      }
      if (discountValue > selectedItem.price_cents) {
        setDiscountError(
          `Amount off can't exceed the item's price of ${formatCents(selectedItem.price_cents)}.`
        );
        return;
      }
    }

    let maxClaimsPerCustomerPerDay: number | null = null;
    if (state.maxClaimsPerCustomerPerDay.trim() !== "") {
      const parsed = parseInt(state.maxClaimsPerCustomerPerDay, 10);
      if (isNaN(parsed) || parsed < 1) {
        setMaxClaimsError("Must be at least 1, or blank for unlimited.");
        return;
      }
      maxClaimsPerCustomerPerDay = parsed;
    }

    const rawSchedule = formToSchedule(state.schedule);
    if (Object.keys(rawSchedule).length === 0) {
      setScheduleFormError("Select at least one day.");
      return;
    }
    const schedErrs = validateRawSchedule(rawSchedule);
    if (schedErrs) {
      setScheduleErrors(schedErrs);
      return;
    }

    onSave({
      name,
      menu_item_id: selectedItem.id,
      buy_quantity: state.buyQuantity,
      get_quantity: state.getQuantity,
      discount_type: state.discountType,
      discount_value: discountValue,
      max_claims_per_customer_per_day: maxClaimsPerCustomerPerDay,
      schedule: rawSchedule,
      status: state.status,
    });
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-4xl sm:max-w-4xl p-0 gap-0 overflow-hidden flex flex-col"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">
            {isEditing ? "Edit Multi Buy Deal" : "Create Multi Buy Promotion"}
          </DialogTitle>

          {/* Header */}
          <div className="flex items-center justify-between px-7 py-4 border-b shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Tag className="size-5 text-primary" />
              </div>
              <div>
                <span className="text-base font-semibold">
                  {isEditing ? "Edit Multi Buy Deal" : "Create Multi Buy Promotion"}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Configure discount rules, items, customer limits, and schedule
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDirty(true);
                  dispatch({ type: "reset", item });
                  setSelectedItem(item?.menu_item ?? null);
                  setNameError(null);
                  setItemError(null);
                  setDiscountError(null);
                  setMaxClaimsError(null);
                  setScheduleErrors({});
                  setScheduleFormError(null);
                }}
                disabled={isPending}
                className="gap-1.5 text-muted-foreground h-8 px-2.5"
              >
                <RotateCcw className="size-3.5" />
                Reset
              </Button>
              <button
                type="button"
                onClick={tryClose}
                className="rounded-md p-1.5 hover:bg-accent text-muted-foreground transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* Body + Footer */}
          <form
            onSubmit={(e) => e.preventDefault()}
            noValidate
            className="flex flex-col flex-1 min-h-0"
          >
            <div className="flex flex-row flex-1 min-h-0 overflow-hidden">
              {/* Left: form */}
              <div className="flex-1 min-w-0 overflow-y-auto px-7 pt-5 pb-5 space-y-5">
                {/* Promotion Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="multi-buy-name">
                    Promotion Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="multi-buy-name"
                    value={state.name}
                    onChange={(e) => {
                      setDirty(true);
                      dispatch({ type: "set", field: "name", value: e.target.value });
                    }}
                    placeholder="e.g. Buy 2 Tacos, Get 1 Free"
                    maxLength={100}
                    disabled={isPending}
                    aria-invalid={!!nameError}
                  />
                  {nameError && <p className="text-xs text-destructive">{nameError}</p>}
                  <p
                    className={`text-xs text-right ${
                      state.name.length > 100 ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {state.name.length}/100
                  </p>
                </div>

                {/* Applicable Item (single-item picker — selecting replaces) */}
                <div className="space-y-1.5" ref={itemSearchRef}>
                  <div className="flex items-center justify-between">
                    <Label>
                      Applicable Item <span className="text-destructive">*</span>
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      Selecting a new item replaces the current one
                    </span>
                  </div>

                  {selectedItem && (
                    <div className="border rounded-md flex items-center gap-3 px-3 py-2.5">
                      {selectedItem.image_url ? (
                        <img
                          src={selectedItem.image_url}
                          alt={selectedItem.name}
                          className="size-10 rounded-md object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-10 rounded-md bg-muted shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{selectedItem.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCents(selectedItem.price_cents)} each
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setDirty(true);
                          setSelectedItem(null);
                        }}
                        disabled={isPending}
                        className="text-muted-foreground hover:text-destructive shrink-0 disabled:opacity-40"
                        aria-label={`Remove ${selectedItem.name}`}
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                      <Input
                        className="pl-8"
                        placeholder="Search menu items..."
                        value={itemSearchInput}
                        disabled={isPending}
                        onChange={(e) => setItemSearchInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && setItemSearchQuery(itemSearchInput)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={isPending}
                      onClick={() => setItemSearchQuery(itemSearchInput)}
                    >
                      Search
                    </Button>
                  </div>
                  {itemSearchQuery.trim() !== "" && (
                    <div className="border rounded-md divide-y divide-border max-h-48 overflow-y-auto">
                      {searchLoading ? (
                        <div className="flex justify-center py-3">
                          <Spinner className="size-4 border-primary" />
                        </div>
                      ) : (searchResults?.data.length ?? 0) === 0 ? (
                        <p className="text-xs text-muted-foreground px-3 py-3">No items found.</p>
                      ) : (
                        searchResults!.data.map((mi) => {
                          const isSelected = selectedItem?.id === mi.id;
                          return (
                            <button
                              key={mi.id}
                              type="button"
                              disabled={isPending || isSelected}
                              onClick={() => selectItem(mi)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors text-left disabled:opacity-60"
                            >
                              {mi.image_url ? (
                                <img
                                  src={mi.image_url}
                                  alt={mi.name}
                                  className="size-9 rounded-md object-cover shrink-0"
                                />
                              ) : (
                                <div className="size-9 rounded-md bg-muted shrink-0" />
                              )}
                              <span className="text-sm truncate flex-1">{mi.name}</span>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {isSelected ? "Selected" : formatCents(mi.price_cents)}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                  {itemError && <p className="text-xs text-destructive">{itemError}</p>}
                </div>

                {/* Customer Buys / Gets */}
                <div className="border rounded-lg grid grid-cols-2 divide-x bg-muted-foreground/[0.03]">
                  <div className="px-4 pt-3 pb-4 space-y-3">
                    <div>
                      <span className="text-sm font-semibold">Customer Buys</span>
                      <p className="text-xs text-muted-foreground">
                        Quantity required to trigger offer
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setQuantity("buyQuantity", state.buyQuantity - 1)}
                        disabled={isPending || state.buyQuantity <= MIN_QUANTITY}
                        className="size-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                        aria-label="Decrease buy quantity"
                      >
                        <Minus className="size-4" />
                      </button>
                      <span className="text-2xl font-semibold">{state.buyQuantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity("buyQuantity", state.buyQuantity + 1)}
                        disabled={isPending || state.buyQuantity >= MAX_QUANTITY}
                        className="size-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                        aria-label="Increase buy quantity"
                      >
                        <Plus className="size-4" />
                      </button>
                    </div>
                  </div>
                  <div className="px-4 pt-3 pb-4 space-y-3">
                    <div>
                      <span className="text-sm font-semibold">Customer Gets</span>
                      <p className="text-xs text-muted-foreground">Number of reward item(s)</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setQuantity("getQuantity", state.getQuantity - 1)}
                        disabled={isPending || state.getQuantity <= MIN_QUANTITY}
                        className="size-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                        aria-label="Decrease get quantity"
                      >
                        <Minus className="size-4" />
                      </button>
                      <span className="text-2xl font-semibold">{state.getQuantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity("getQuantity", state.getQuantity + 1)}
                        disabled={isPending || state.getQuantity >= MAX_QUANTITY}
                        className="size-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
                        aria-label="Increase get quantity"
                      >
                        <Plus className="size-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Discount type + value */}
                <div className="space-y-1.5">
                  <Label>
                    Discount on the &quot;get&quot; unit <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex items-center gap-3 flex-wrap">
                    <SegmentedToggle
                      value={state.discountType}
                      onChange={(t) => {
                        setDirty(true);
                        setDiscountError(null);
                        dispatch({ type: "setDiscountType", value: t });
                      }}
                      options={[
                        { value: "percent", label: "% Off" },
                        { value: "amount", label: "$ Off" },
                      ]}
                      disabled={isPending}
                    />
                    <div className="relative flex-1 min-w-[8rem]">
                      {state.discountType === "amount" && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none select-none">
                          $
                        </span>
                      )}
                      <Input
                        type="text"
                        inputMode={state.discountType === "percent" ? "numeric" : "decimal"}
                        placeholder={state.discountType === "amount" ? "0.00" : "e.g. 50"}
                        value={state.discountValue}
                        aria-invalid={!!discountError}
                        disabled={isPending}
                        fullWidth={false}
                        className={state.discountType === "amount" ? "w-full pl-6" : "w-full pr-8"}
                        endIcon={
                          state.discountType === "percent" ? (
                            <span className="text-sm text-muted-foreground">%</span>
                          ) : undefined
                        }
                        onChange={(e) => {
                          setDirty(true);
                          setDiscountError(null);
                          const raw =
                            state.discountType === "amount"
                              ? e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1")
                              : e.target.value.replace(/\D/g, "");
                          dispatch({ type: "set", field: "discountValue", value: raw });
                        }}
                      />
                    </div>
                    {!(state.discountType === "percent" && state.discountValue === "100") && (
                      <button
                        type="button"
                        onClick={() => {
                          setDirty(true);
                          setDiscountError(null);
                          dispatch({ type: "setDiscountType", value: "percent" });
                          dispatch({ type: "set", field: "discountValue", value: "100" });
                        }}
                        disabled={isPending}
                        className="text-xs border border-border rounded-md px-3 h-9 text-foreground hover:bg-muted transition-colors shrink-0 disabled:opacity-40"
                      >
                        Set as free
                      </button>
                    )}
                  </div>
                  {discountError && <p className="text-xs text-destructive">{discountError}</p>}
                </div>

                {/* Schedule */}
                <div className="border-y py-5 bg-muted-foreground/[0.07] -mx-7 px-7">
                  <DealScheduleInput
                    schedule={formToSchedule(state.schedule)}
                    onChange={(raw) => {
                      setDirty(true);
                      dispatch({ type: "setSchedule", value: scheduleToForm(raw) });
                      setScheduleErrors({});
                      setScheduleFormError(null);
                    }}
                    error={scheduleFormError ?? Object.values(scheduleErrors)[0]}
                  />
                </div>

                {/* Max claims per customer per day (radio-style cards) */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Max claims per customer per day</span>
                    <span className="text-xs text-muted-foreground">Prevents promotion abuse</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setDirty(true);
                        setMaxClaimsError(null);
                        dispatch({
                          type: "set",
                          field: "maxClaimsPerCustomerPerDay",
                          value: "",
                        });
                      }}
                      disabled={isPending}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        state.maxClaimsPerCustomerPerDay === ""
                          ? "border-primary"
                          : "border-border hover:border-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                          state.maxClaimsPerCustomerPerDay === ""
                            ? "border-primary"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {state.maxClaimsPerCustomerPerDay === "" && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Unlimited</p>
                        <p className="text-xs text-muted-foreground">No daily order restriction</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDirty(true);
                        if (state.maxClaimsPerCustomerPerDay === "")
                          dispatch({
                            type: "set",
                            field: "maxClaimsPerCustomerPerDay",
                            value: "1",
                          });
                      }}
                      disabled={isPending}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        state.maxClaimsPerCustomerPerDay !== ""
                          ? "border-primary"
                          : "border-border hover:border-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                          state.maxClaimsPerCustomerPerDay !== ""
                            ? "border-primary"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {state.maxClaimsPerCustomerPerDay !== "" && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Limit Per User</p>
                        <p className="text-xs text-muted-foreground">Fixed number of redemptions</p>
                      </div>
                    </button>
                  </div>
                  {state.maxClaimsPerCustomerPerDay !== "" && (
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={state.maxClaimsPerCustomerPerDay}
                        aria-invalid={!!maxClaimsError}
                        disabled={isPending}
                        fullWidth={false}
                        className="w-20"
                        onChange={(e) => {
                          setDirty(true);
                          setMaxClaimsError(null);
                          dispatch({
                            type: "set",
                            field: "maxClaimsPerCustomerPerDay",
                            value: e.target.value.replace(/\D/g, ""),
                          });
                        }}
                      />
                      <span className="text-sm text-muted-foreground">
                        redemption(s) per guest per 24 hours
                      </span>
                    </div>
                  )}
                  {maxClaimsError && <p className="text-xs text-destructive">{maxClaimsError}</p>}
                </div>

                {/* Status — edit only; new Multi Buy deals are always created active */}
                {isEditing && (
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <SegmentedToggle
                      value={state.status}
                      onChange={(s) => {
                        setDirty(true);
                        dispatch({ type: "setStatus", value: s });
                      }}
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ]}
                      disabled={isPending}
                    />
                  </div>
                )}

                {apiError && <p className="text-sm text-destructive">{apiError}</p>}
              </div>

              {/* Right: live preview */}
              <div className="w-[360px] shrink-0 border-l overflow-y-auto bg-slate-100">
                <div className="px-5 py-5 space-y-4">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    <span className="size-2 rounded-full bg-green-500 shrink-0" />
                    Live Customer Preview
                  </div>

                  {!selectedItem ? (
                    <div className="rounded-xl border border-dashed border-slate-300 h-52 flex items-center justify-center">
                      <p className="text-sm text-slate-400 text-center px-4">
                        Select an item to see a live preview
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Image + info — unified card */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                        {/* Image */}
                        <div className="relative aspect-video bg-slate-200">
                          {selectedItem.image_url ? (
                            <img
                              src={selectedItem.image_url}
                              alt={selectedItem.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                              <Tag className="size-10 text-slate-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                          <div className="absolute top-2.5 left-2.5 flex items-center gap-1 bg-black/50 text-white px-2 py-0.5 rounded-md text-[10px] font-semibold backdrop-blur-sm">
                            <Zap className="size-3 fill-current" />
                            MULTI BUY DEAL
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 flex items-end justify-end gap-2">
                            {rulePreview && (
                              <span className="shrink-0 bg-primary text-primary-foreground text-sm font-bold px-2.5 py-1 rounded-md whitespace-nowrap">
                                {rulePreview}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Deal info */}
                        <div className="p-4 space-y-3">
                          <div className="space-y-0.5">
                            <p className="text-sm font-semibold leading-snug text-slate-900">
                              {state.name.trim()
                                ? state.name.trim()
                                : rulePreview
                                  ? rulePreview.charAt(0).toUpperCase() + rulePreview.slice(1)
                                  : `Buy ${state.buyQuantity} ${selectedItem.name}, Get ${state.getQuantity}`}
                            </p>
                            <p className="text-xs text-slate-500 line-clamp-2">
                              {selectedItem.name}
                              {selectedItem.description ? ` • ${selectedItem.description}` : ""}
                            </p>
                          </div>

                          {preview ? (
                            <>
                              <div className="space-y-1 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">Add to Cart:</span>
                                  <span className="font-medium text-slate-800">
                                    {preview.addToCartCount} {selectedItem.name}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500">Automatic discount:</span>
                                  <span className="font-medium text-primary">
                                    -{formatCents(preview.discountCents)}
                                  </span>
                                </div>
                              </div>
                              <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                    Total Customer Pays
                                  </p>
                                  <div className="flex items-baseline gap-1.5 mt-0.5">
                                    <span className="text-xl font-bold text-slate-900">
                                      {formatCents(preview.revenueCents)}
                                    </span>
                                    <span className="text-sm text-slate-400 line-through">
                                      {formatCents(preview.fullValueCents)}
                                    </span>
                                  </div>
                                </div>
                                <div className="shrink-0 bg-slate-900 text-white text-xs font-semibold px-4 py-2 rounded-lg">
                                  Add Deal
                                </div>
                              </div>
                            </>
                          ) : (
                            <p className="text-xs text-slate-400">
                              Enter a discount value to see the full preview.
                            </p>
                          )}
                        </div>
                      </div>
                      {/* end unified card */}

                      {/* Deal Economics */}
                      {preview && (
                        <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                              <LayoutGrid className="size-3.5 text-slate-400" />
                              Deal Economics Breakdown
                            </div>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              Per Order
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="rounded-lg bg-slate-50 p-2">
                              <p className="text-[10px] text-slate-400 mb-1">Full Value</p>
                              <p className="text-sm font-semibold text-slate-800">
                                {formatCents(preview.fullValueCents)}
                              </p>
                            </div>
                            <div className="rounded-lg bg-red-50 p-2">
                              <p className="text-[10px] text-red-500 mb-1">Merchant Cost</p>
                              <p className="text-sm font-semibold text-red-500">
                                -{formatCents(preview.discountCents)}
                              </p>
                            </div>
                            <div className="rounded-lg bg-green-50 p-2">
                              <p className="text-[10px] text-green-600 mb-1">Revenue</p>
                              <p className="text-sm font-semibold text-green-600">
                                {formatCents(preview.revenueCents)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-7 py-3.5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5 text-xs">
                {isReadyToPublish ? (
                  <>
                    <CheckCircle className="size-3.5 text-green-600 shrink-0" />
                    <span className="text-muted-foreground">
                      Validation passed • Ready to publish
                    </span>
                  </>
                ) : hasAttemptedSubmit ? (
                  <div className="flex items-center gap-1.5 bg-destructive/10 text-destructive border border-destructive/20 rounded-md px-2.5 py-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span className="font-medium">{nextRequiredStep}</span>
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button type="button" variant="outline" onClick={tryClose} disabled={isPending}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={isPending}>
                      {isPending ? (
                        <Spinner className="size-4 border-primary-foreground" />
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={tryClose} disabled={isPending}>
                      Discard
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSave}
                      disabled={isPending}
                      className="gap-1.5"
                    >
                      {isPending ? (
                        <Spinner className="size-4 border-primary-foreground" />
                      ) : (
                        <>
                          <span>Publish Multi Buy Deal</span>
                          <ArrowRight className="size-4" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
